import React from 'react';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { isString, isFunction, omitFromObject } from 'utils/helpers';
import {
  DropdownItem,
  DropdownTextContainer,
  IconContainer,
  StyledSemanticDropdown,
} from './Dropdown.styled';

const defaultProps = {
  checkSelected: false,
  clearable: false,
  disabled: false,
  fluid: false,
  hasIcons: false,
  hideTextOnSelection: false,
  icon: 'dropdown',
  inline: false,
  isButton: false,
  isError: false,
  isLoading: false,
  noResultsMessage: 'No Results Found.',
  placeholder: '',
  search: false,
  searchCaseSensitive: false,
  simple: false,
  trigger: null,
  upward: false,
};

/**
 * Wrapper component of [Semantic UI Dropdown](https://react.semantic-ui.com/modules/dropdown/).
 * Provides easy options for adding headers, dividers and sections to dropdown.
 * DropdownProps prop types can be found in ./types
 */
export default class Dropdown extends React.Component {
  /**
   * generateItem
   * Creates the dropdown item.
   * @param { Object } itemProps: Props of the item.
   * @returns { React.Component } The rendered item.
   */
  generateItem = (itemProps) => {
    const { checkSelected, hasIcons, hideTextOnSelection, menuItem } = this.props;
    const { text, subText, icon, iconClassName } = itemProps;
    const iconName = checkSelected ? 'check' : icon;
    const iconComponent = isString(iconName) ? <SemanticUI.Icon name={iconName} /> : icon;
    const hasIcon = !!icon || hasIcons || !!iconName;
    return menuItem ? undefined : (
      <DropdownItem className={checkSelected ? 'cssCheckSelected' : ''} data-has-icon={hasIcon}>
        {hasIcon && <IconContainer className={iconClassName}>{iconComponent}</IconContainer>}
        <DropdownTextContainer $hideText={hideTextOnSelection}>
          <div className="cssDropdownItemText">{text}</div>
          <div className="cssDropdownItemSubtext">{subText}</div>
        </DropdownTextContainer>
      </DropdownItem>
    );
  };

  /**
   * generateOptions
   * Formats the options array to pass to the dropdown.
   * @param { Array } options: List of options to format for the dropdown.
   * @returns { Array } The formatted options.
   */
  generateOptions = (options) =>
    options.map((option, index) => {
      const {
        description,
        header,
        headerDivider,
        icon,
        divider,
        section,
        sectionValues,
        subText,
        text,
        value,
        disabled,
        ...itemProps
      } = option;
      const key = `dropdown-item-${index}`;
      const displayValue = text || value;

      if (header || section) {
        const className = `cssDropdownHeader ${section ? 'cssDropdownSection' : ''}`;
        return {
          ...itemProps,
          key,
          active: false,
          className,
          disabled: true,
          selected: false,
          text: displayValue,
          value,
        };
      }

      if (divider) {
        return {
          key,
          active: false,
          className: 'cssDropdownDivider',
          disabled: true,
          selected: false,
        };
      }

      return {
        ...itemProps,
        key,
        description,
        text: this.generateItem({ icon, subText, text: displayValue, value }),
        name: displayValue,
        value,
        disabled,
      };
    });

  /**
   * handleSearch
   * Default search function
   * @param { Array } items: The items in the dropdown.
   * @param { String } searchValue: The search string value.
   * @returns { Array } The filtered items matching the searchValue.
   */
  handleSearch = (items = [], searchValue) => {
    const { search, searchCaseSensitive } = this.props;
    if (isFunction(search)) {
      return search(items, searchValue);
    }

    return items.filter((item) => {
      const { name, text, value } = item;
      const displayValue = name || value || text || '';
      const searchString = searchCaseSensitive ? displayValue : displayValue?.toLowerCase?.();
      const searchInput = searchCaseSensitive ? searchValue : searchValue?.toLowerCase?.();
      return searchString?.indexOf?.(searchInput) !== -1;
    });
  };

  render() {
    const {
      className,
      clearable,
      defaultValue,
      disabled,
      fluid,
      header,
      icon,
      inline,
      isButton,
      isError,
      isLoading,
      menuItem,
      onChange,
      options,
      placeholder,
      search,
      simple,
      trigger,
      upward,
      value,
      ...otherProps
    } = this.props;
    return (
      <StyledSemanticDropdown
        {...omitFromObject(
          otherProps,
          'checkSelected',
          'hasIcons',
          'hideTextOnSelection',
          'options',
          'onChange',
          'searchCaseSensitive',
          'selection',
        )}
        button={isButton}
        className={className}
        clearable={clearable}
        defaultValue={defaultValue}
        disabled={disabled}
        error={isError}
        fluid={fluid}
        header={header}
        icon={trigger ? null : icon}
        item={menuItem}
        loading={isLoading}
        options={this.generateOptions(options || [])}
        onChange={onChange}
        placeholder={placeholder}
        search={search ? this.handleSearch : undefined}
        selection={!simple && !isButton && !trigger && !inline}
        simple={simple}
        trigger={trigger}
        upward={upward}
        value={value}
      />
    );
  }
}

Object.keys(SemanticUI.Dropdown).forEach((key) => {
  Dropdown[key] = SemanticUI.Dropdown[key];
});

Dropdown.defaultProps = defaultProps;
